import { useMemo, useCallback } from 'react';
import { parseISO, startOfDay, differenceInDays, isValid } from 'date-fns';

export interface EarningsData {
  t: string; // ticker symbol
  d: string; // earnings date (ISO string)
  epsA: number | null; // actual EPS
  epsE: number | null; // expected EPS
  revA: number | null; // actual revenue
  revE: number | null; // expected revenue
  lu: string; // last updated (ISO string)
}

export interface EarningsMap {
  [ticker: string]: EarningsData;
}

export type EnrichedTickerData<T = any> = T & {
  earningsData: EarningsData | null;
  earningsDate: string | null;
  daysToEarnings: number | null;
  hasEarnings: boolean;
  hasUpcomingEarnings: boolean;
  epsExpected: number | null;
  revenueExpected: number | null;
};

interface UseEarningsDataReturn {
  getEarningsData: (ticker: string | null | undefined) => EarningsData | null;
  hasEarnings: (ticker: string | null | undefined) => boolean;
  getDaysToEarnings: (ticker: string | null | undefined) => number | null;
  hasUpcomingEarnings: (ticker: string | null | undefined) => boolean;
  filterUpcomingOnly: <T extends { ticker: string }>(tickers: T[]) => T[];
  getAllUpcomingTickers: () => string[];
  enrichTickerWithEarnings: <T extends { ticker: string | null | undefined }>(
    ticker: T,
  ) => EnrichedTickerData<T>;
  hasEarningsMap: boolean;
}

interface IndexedEarnings {
  data: EarningsMap;
  index: Map<string, string>;
}

// EarningsProvider publishes a new response object on refresh. All visible rows consume
// that same snapshot; weak keys let its index go with it when the response is replaced.
const earningsIndexes = new WeakMap<EarningsMap, IndexedEarnings>();
const emptyEarnings: IndexedEarnings = { data: {}, index: new Map() };

export function useEarningsData(earningsMap?: EarningsMap | null): UseEarningsDataReturn {
  const indexedEarningsMap = useMemo(() => {
    if (!earningsMap) return emptyEarnings;

    const cached = earningsIndexes.get(earningsMap);
    if (cached) return cached;

    const index = new Map<string, string>();
    Object.keys(earningsMap).forEach((ticker) => {
      index.set(ticker.toLowerCase(), ticker);
      index.set(ticker, ticker);
    });

    const indexed = { data: earningsMap, index };
    earningsIndexes.set(earningsMap, indexed);
    return indexed;
  }, [earningsMap]);

  const getEarningsData = useCallback(
    (ticker: string | null | undefined): EarningsData | null => {
      if (!ticker || typeof ticker !== 'string' || !indexedEarningsMap.data) return null;

      const tickerKey =
        indexedEarningsMap.index.get(ticker) || indexedEarningsMap.index.get(ticker.toLowerCase());

      return tickerKey ? indexedEarningsMap.data[tickerKey] || null : null;
    },
    [indexedEarningsMap],
  );

  const hasEarnings = useCallback(
    (ticker: string | null | undefined): boolean => {
      return getEarningsData(ticker) !== null;
    },
    [getEarningsData],
  );

  const getDaysToEarnings = useCallback(
    (ticker: string | null | undefined): number | null => {
      const earningsData = getEarningsData(ticker);
      if (!earningsData?.d) return null;

      try {
        const earningsDate = startOfDay(parseISO(earningsData.d));
        const today = startOfDay(new Date());

        if (!isValid(earningsDate)) return null;

        return differenceInDays(earningsDate, today);
      } catch (error) {
        console.warn(`Invalid date for ticker ${ticker}:`, earningsData.d);
        return null;
      }
    },
    [getEarningsData],
  );

  const hasUpcomingEarnings = useCallback(
    (ticker: string | null | undefined): boolean => {
      const daysToEarnings = getDaysToEarnings(ticker);
      return daysToEarnings !== null && daysToEarnings >= 0;
    },
    [getDaysToEarnings],
  );

  const filterUpcomingOnly = useCallback(
    <T extends { ticker: string }>(tickers: T[]): T[] => {
      if (!Array.isArray(tickers)) return [];

      return tickers.filter((ticker) => {
        return hasUpcomingEarnings(ticker.ticker);
      });
    },
    [hasUpcomingEarnings],
  );

  const getAllUpcomingTickers = useCallback((): string[] => {
    if (!indexedEarningsMap.data) return [];

    return Object.keys(indexedEarningsMap.data).filter((ticker) => hasUpcomingEarnings(ticker));
  }, [indexedEarningsMap.data, hasUpcomingEarnings]);

  // Updated to handle nullable ticker
  const enrichTickerWithEarnings = useCallback(
    <T extends { ticker: string | null | undefined }>(ticker: T): EnrichedTickerData<T> => {
      const tickerSymbol = ticker.ticker;
      const earningsData = tickerSymbol ? getEarningsData(tickerSymbol) : null;
      const daysToEarnings = tickerSymbol ? getDaysToEarnings(tickerSymbol) : null;

      return {
        ...ticker,
        earningsData,
        earningsDate: earningsData?.d || null,
        daysToEarnings,
        hasEarnings: !!earningsData,
        hasUpcomingEarnings: tickerSymbol ? hasUpcomingEarnings(tickerSymbol) : false,
        epsExpected: earningsData?.epsE || null,
        revenueExpected: earningsData?.revE || null,
      };
    },
    [getEarningsData, getDaysToEarnings, hasUpcomingEarnings],
  );

  return {
    getEarningsData,
    hasEarnings,
    getDaysToEarnings,
    hasUpcomingEarnings,
    filterUpcomingOnly,
    getAllUpcomingTickers,
    enrichTickerWithEarnings,
    hasEarningsMap: indexedEarningsMap.index.size > 0,
  };
}
